import {Ionicons} from '@expo/vector-icons';
import {HeaderBackButton} from '@react-navigation/elements';
import {
  HeaderStyleInterpolators,
  StackNavigationOptions,
  TransitionPresets
} from '@react-navigation/stack';
import {Button, CloseIcon, Pressable, useTheme} from 'native-base';
import React, {useEffect} from 'react';
import {Platform, useWindowDimensions} from 'react-native';
import {t} from 'react-native-tailwindcss';
import {AccountStatus, useAccountQuery, useOnAccountUpdatedSubscription} from '../api/generated';
import {AccountContext} from '../lib/accountContext';
import i18n from '../lib/i18n';
import {CreatePaymentScreens, createPaymentStackLinking} from './CreatePaymentScreens';
import {EditAccountScreen} from './EditAccountScreen';
import {HomeScreen} from './HomeScreen';
import {IdSessionScreen} from './IdSessionScreen';
import {Nav, navigate, Stack} from './navigation';
import {NotFoundScreen} from './NotFoundScreen';
import {PaymentScreen} from './PaymentScreen';
import {PaymentsScreen} from './PaymentsScreen';
import {PayoutScreen} from './PayoutScreen';
import {RechargeScreen} from './RechargeScreen';
import {RechargeViewScreen} from './RechargeViewScreen';
import {ReceivePaymentScreen} from './ReceivePaymentScreen';
import {ScanPaymentScreen} from './ScanPaymentScreen';
import {SettingsScreen} from './SettingsScreen';
import dayjs from 'dayjs';
import {checkNewAccount} from '../hooks/useNewAccount';

type Props = {};

export const mainStackLinking = {
  initialRouteName: Nav.HomeScreen,
  screens: {
    [Nav.HomeScreen]: '/',
    [Nav.IdSessionScreen]: '/verification',
    [Nav.PaymentScreen]: '/payments/:paymentId',
    [Nav.RechargeScreen]: '/recharge',
    [Nav.PayoutScreen]: '/payout',
    [Nav.ScanPaymentScreen]: '/scan',
    [Nav.ReceivePaymentScreen]: '/receive',
    [Nav.RechargeViewScreen]: '/recharge/checkout',
    [Nav.PaymentsScreen]: '/payments',
    [Nav.SettingsScreen]: '/settings',
    [Nav.EditAccountScreen]: '/account/edit',
    [Nav.CreatePaymentScreens]: createPaymentStackLinking,
    [Nav.NotFound]: '*'
  }
};

export const modalOptions: StackNavigationOptions = {
  headerLeft: (props) => {
    if (Platform.OS !== 'web') {
      return (
        <Pressable {...props}>
          <CloseIcon size="5" style={t.mL3} color="black" />
        </Pressable>
      );
    }
    return <HeaderBackButton {...props} style={t.mL3} />;
  },
  presentation: Platform.OS === 'web' ? 'card' : 'modal',
  animationEnabled: true
};

export const MainStack: React.FC<Props> = () => {
  const {data, loading} = useAccountQuery();
  const {width} = useWindowDimensions();
  const {colors} = useTheme();

  useOnAccountUpdatedSubscription();

  const accountStatus = data?.account?.status;
  const isNew = checkNewAccount(data?.account);
  const isVerified = loading || accountStatus === AccountStatus.VERIFIED;

  useEffect(() => {
    if (!isVerified) {
      navigate(Nav.IdSessionScreen);
    }
  }, [isVerified]);

  return (
    <AccountContext.Provider value={data?.account}>
      <Stack.Navigator
        initialRouteName={isVerified ? Nav.HomeScreen : Nav.IdSessionScreen}
        screenOptions={{
          cardStyle: {flex: 1},
          headerTitleAlign: 'center',
          headerBackTitleVisible: false,
          headerTintColor: colors.primary[600],
          headerStyleInterpolator: HeaderStyleInterpolators.forUIKit,
          headerTitleStyle: {
            maxWidth: width - 140,
            color: 'black'
          }
        }}>
        {isVerified || isNew ? (
          <>
            <Stack.Screen
              name={Nav.HomeScreen}
              component={HomeScreen}
              options={({navigation}) => ({
                title: i18n.t('home'),
                headerRight: () => (
                  <Button variant="link" onPress={() => navigation.navigate(Nav.SettingsScreen)}>
                    <Ionicons style={t.mR2} name="settings-outline" size={24} color="black" />
                  </Button>
                )
              })}
            />
            <Stack.Screen
              name={Nav.SettingsScreen}
              component={SettingsScreen}
              options={{
                title: i18n.t('settings'),
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.CreatePaymentScreens}
              component={CreatePaymentScreens}
              options={{
                title: i18n.t('createPayment'),
                headerShown: false,
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.PaymentsScreen}
              component={PaymentsScreen}
              options={{title: i18n.t('payments'), ...TransitionPresets.SlideFromRightIOS}}
            />
            <Stack.Screen
              name={Nav.PaymentScreen}
              component={PaymentScreen}
              options={{
                title: i18n.t('payment'),
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.RechargeScreen}
              component={RechargeScreen}
              options={{
                title: i18n.t('recharge'),
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.RechargeViewScreen}
              component={RechargeViewScreen}
              options={{
                title: i18n.t('recharge'),
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.PayoutScreen}
              component={PayoutScreen}
              options={{
                title: i18n.t('payout'),
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.ScanPaymentScreen}
              component={ScanPaymentScreen}
              options={{
                title: i18n.t('scanToPay'),
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.ReceivePaymentScreen}
              component={ReceivePaymentScreen}
              options={{
                title: i18n.t('receive'),
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.EditAccountScreen}
              component={EditAccountScreen}
              options={{
                title: i18n.t('editAccount'),
                ...modalOptions
              }}
            />
            <Stack.Screen
              name={Nav.IdSessionScreen}
              component={IdSessionScreen}
              options={{
                title: i18n.t('idSession'),
                headerLeft: () => null,
                presentation: Platform.OS === 'web' ? 'card' : 'modal',
                animationEnabled: true
              }}
            />
          </>
        ) : (
          <>
            <Stack.Screen
              name={Nav.IdSessionScreen}
              component={IdSessionScreen}
              options={{
                title: i18n.t('idSession'),
                headerLeft: () => null,
                presentation: Platform.OS === 'web' ? 'card' : 'modal',
                animationEnabled: true
              }}
            />
          </>
        )}
        <Stack.Screen name={Nav.NotFound} component={NotFoundScreen} options={{title: '404'}} />
      </Stack.Navigator>
    </AccountContext.Provider>
  );
};
